import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import FilterBar from '../../components/common/FilterBar';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { carePlanAPI, partyAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const fmtErr = (d, fb) => { if (!d) return fb; if (typeof d === 'string') return d; if (Array.isArray(d)) return d.map(e => e?.msg ? `${(e.loc || []).slice(-1)[0]}: ${e.msg}` : JSON.stringify(e)).join('; '); return d.msg || d.message || JSON.stringify(d); };
const inp = { width: '100%', padding: '9px 12px', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 5 };

const STATUSES = ['DRAFT', 'ACTIVE', 'ON_HOLD', 'COMPLETED', 'REVOKED'];

const FILTER_DEF = [
  { key: 'status', label: 'Status', type: 'select', options: STATUSES },
];
const BLANK_F = { status: '' };

// Color bands for status summary
const STATUS_COLOR = {
  DRAFT:     '#f1f5f9',
  ACTIVE:    '#d1fae5',
  ON_HOLD:   '#fef9c3',
  COMPLETED: '#f6d6e6',
  REVOKED:   '#fee2e2',
};

export default function CarePlans() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isPatientOnly = roles.includes('PATIENT') && !roles.some(r => ['ADMIN', 'DOCTOR', 'NURSE', 'BILLING'].includes(r));
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const PER = 50;
  const [filters, setFilters] = useState(BLANK_F);

  const [patients, setPatients] = useState([]);
  const [doctors,  setDoctors]  = useState([]);

  // Create — opened with ?create=1&request=<id>&patient=<id> from the doctor's
  // Care Requests queue: the plan links to the request, and activating it
  // auto-dispatches the closest skill-matched nurse.
  const [params, setParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(params.get('create') === '1');
  const [cf, setCf] = useState({
    patient_id: params.get('patient') || '', doctor_id: '',
    patient_name: params.get('pname') || '',
    care_request_id: params.get('request') || '',
    diagnosis: '', icd10_code: '', care_instructions: '', start_date: '', end_date: '',
  });
  const isDoctorUser = (user?.roles || []).includes('DOCTOR');
  const [cErr, setCErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [dispatchInfo, setDispatchInfo] = useState(null); // result banner after create

  // Edit
  const [editRow, setEditRow] = useState(null);
  const [ef, setEf]           = useState({ status: 'ACTIVE', care_instructions: '', end_date: '' });
  const [eErr, setEErr]       = useState('');
  const [eSaving, setESaving] = useState(false);

  // Revoke
  const [revokeRow, setRevokeRow]   = useState(null);
  const [revokeLoading, setRevoke]  = useState(false);

  const buildParams = useCallback(() => {
    const p = { page, per_page: PER };
    if (filters.status) p.status = filters.status;
    return p;
  }, [filters, page]);

  const load = useCallback(() => {
    setLoading(true);
    carePlanAPI.list(buildParams())
      .then(r => { setRows(r.data || []); setTotal(r.meta?.total || 0); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filters]);
  useEffect(() => {
    if (isPatientOnly) return; // party lists are staff-only; patient view is read-only
    partyAPI.list({ role: 'PATIENT', per_page: 200 }).then(r => setPatients(r.data || []));
    partyAPI.list({ role: 'DOCTOR',  per_page: 200 }).then(r => setDoctors(r.data || []));
  }, [isPatientOnly]);

  const handleCreate = async e => {
    e.preventDefault(); setSaving(true); setCErr('');
    try {
      const resp = await carePlanAPI.create({
        patient_id:       cf.patient_id,
        doctor_id:        cf.doctor_id        || undefined,
        care_request_id:  cf.care_request_id  || undefined,
        diagnosis:        cf.diagnosis,
        icd10_code:       cf.icd10_code       || undefined,
        care_instructions:cf.care_instructions|| undefined,
        start_date:       cf.start_date       || undefined,
        end_date:         cf.end_date         || undefined,
        // Request-linked plans go live immediately so the nurse dispatch
        // fires; standalone plans stay DRAFT for review as before.
        status:           cf.care_request_id ? 'ACTIVE' : 'DRAFT',
      });
      const d = resp?.data ?? resp;
      setDispatchInfo(d?.dispatch || null);
      setShowCreate(false);
      setParams({}, { replace: true });
      setCf({ patient_id: '', doctor_id: '', care_request_id: '', diagnosis: '', icd10_code: '', care_instructions: '', start_date: '', end_date: '' });
      load();
    } catch (err) { setCErr(fmtErr(err.response?.data?.detail, 'Failed to create care plan')); }
    finally { setSaving(false); }
  };

  const openEdit = row => {
    setEditRow(row);
    setEf({ status: row.status, care_instructions: row.care_instructions || '', end_date: row.end_date ? row.end_date.slice(0, 10) : '' });
    setEErr('');
  };
  const handleEdit = async e => {
    e.preventDefault(); setESaving(true); setEErr('');
    try {
      await carePlanAPI.update(editRow.care_plan_id, {
        status:            ef.status,
        care_instructions: ef.care_instructions || undefined,
        end_date:          ef.end_date          || undefined,
      });
      setEditRow(null); load();
    } catch (err) { setEErr(fmtErr(err.response?.data?.detail, 'Failed to update care plan')); }
    finally { setESaving(false); }
  };

  const handleRevoke = async () => {
    setRevoke(true);
    try { await carePlanAPI.revoke(revokeRow.care_plan_id); setRevokeRow(null); load(); }
    catch {} finally { setRevoke(false); }
  };

  const counts = STATUSES.reduce((acc, s) => { acc[s] = rows.filter(r => r.status === s).length; return acc; }, {});

  const columns = [
    { key: 'diagnosis',   label: 'Diagnosis',  render: v => <strong style={{ color: '#2b1b22' }}>{v || '—'}</strong> },
    { key: 'icd10_code',  label: 'ICD-10',     render: v => v ? <span style={{ padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 8, fontSize: 11, fontWeight: 600 }}>{v}</span> : '—' },
    { key: 'patient_id',  label: 'Patient',    render: (_, r) => isPatientOnly ? (user?.name || 'You') : (r.patient_name || r.patient_id?.slice(0, 8)  || '—') },
    { key: 'doctor_id',   label: 'Doctor',     render: (_, r) => r.doctor_name  || r.doctor_id?.slice(0, 8)   || '—' },
    { key: 'start_date',  label: 'Start',      render: v => v ? new Date(v).toLocaleDateString() : '—' },
    { key: 'end_date',    label: 'End',        render: v => v ? new Date(v).toLocaleDateString() : '—' },
    { key: 'status',      label: 'Status',     render: v => <Badge value={v} /> },
    ...(isPatientOnly ? [] : [{
      key: '_actions', label: 'Actions', render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => openEdit(row)} style={btnSm('#94335a')} disabled={['REVOKED', 'COMPLETED'].includes(row.status)}>✏️</button>
          <button onClick={() => setRevokeRow(row)} style={btnSm('#ef4444')} disabled={['REVOKED', 'COMPLETED'].includes(row.status)}>✕ Revoke</button>
        </div>
      ),
    }]),
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#2b1b22', margin: 0 }}>{isPatientOnly ? 'My Care Plans' : 'Care Plans'}</h1>
          <p style={{ color: '#8b7681', fontSize: 13, marginTop: 4 }}>{isPatientOnly ? 'Your care plans, prescribed by your care team.' : `${total} care plans · ICD-10 coded`}</p>
        </div>
        {!isPatientOnly && <button onClick={() => setShowCreate(true)} style={btnPrimary('#94335a')}>+ New Care Plan</button>}
      </div>

      {/* Auto-dispatch outcome after a request-linked plan goes live */}
      {dispatchInfo && (
        <div style={{
          marginBottom: 16, padding: '12px 16px', borderRadius: 10, fontSize: 13,
          background: dispatchInfo.assigned ? '#d1fae5' : '#fef9c3',
          border: `1px solid ${dispatchInfo.assigned ? '#6ee7b7' : '#fde68a'}`,
          color: '#2b1b22', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        }}>
          <span>
            {dispatchInfo.assigned ? (
              <>🚑 <strong>{dispatchInfo.nurse_name}</strong> auto-assigned
                {dispatchInfo.distance_km != null && <> — {dispatchInfo.distance_km} km away</>}
                {dispatchInfo.matched_skills?.length > 0 && <> · skills: {dispatchInfo.matched_skills.join(', ')}</>}
                {' '}· match score {dispatchInfo.score}/100. A visit has been scheduled.</>
            ) : (
              <>⚠️ Care plan saved, but no nurse was auto-assigned: {dispatchInfo.reason}</>
            )}
          </span>
          <button onClick={() => setDispatchInfo(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 15, color: '#8b7681' }}>✕</button>
        </div>
      )}

      {/* Status summary bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        {STATUSES.map(s => (
          <div key={s} style={{ background: STATUS_COLOR[s] || '#f1f5f9', borderRadius: 8, padding: '8px 14px', textAlign: 'center', minWidth: 80 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#2b1b22' }}>{counts[s]}</div>
            <div style={{ fontSize: 10, color: '#475569', fontWeight: 600 }}>{s}</div>
          </div>
        ))}
      </div>

      <FilterBar filters={FILTER_DEF} values={filters} onChange={(k, v) => setFilters(f => ({ ...f, [k]: v }))} onClear={() => setFilters(BLANK_F)} />

      {loading ? <div style={{ color: '#8b7681', padding: 20 }}>Loading…</div> : <Table columns={columns} data={rows} />}
      <Pagination page={page} total={total} per={PER} onPage={setPage} />

      {/* ── Create Modal ── */}
      {showCreate && (
        <Modal title="New Care Plan" onClose={() => setShowCreate(false)} fullscreen>
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
          {cErr && <ErrBox msg={cErr} />}
          {cf.care_request_id && (
            <div style={{
              marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 12.5,
              background: '#fdeef5', border: '1px solid #ecc0d6', color: '#7b1e3c',
            }}>
              🔗 Linked to care request <code style={{ fontSize: 11 }}>{cf.care_request_id.slice(0, 8)}…</code> —
              saving this plan activates it and <strong>automatically dispatches the closest skill-matched nurse</strong> to the patient.
            </div>
          )}
          <form onSubmit={handleCreate}>
            <Grid2>
              <div>
                <label style={lbl}>Patient *</label>
                {cf.care_request_id && cf.patient_id ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fdeef5', border: '1px solid #f0c8da', borderRadius: 16, fontSize: 13, fontWeight: 600, color: '#7b1e3c' }}>
                    {cf.patient_name || (patients.find(p => p.party_id === cf.patient_id)?.name) || cf.patient_id.slice(0, 8)} · from request
                  </span>
                ) : (
                  <select style={inp} value={cf.patient_id} onChange={e => setCf(f => ({ ...f, patient_id: e.target.value }))} required>
                    <option value="">— Select patient —</option>
                    {patients.map(p => <option key={p.party_id} value={p.party_id}>{p.name}</option>)}
                  </select>
                )}
              </div>
              <div>
                <label style={lbl}>Prescribing Doctor</label>
                {isDoctorUser ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#f1f5f9', border: '1px solid #ece3e7', borderRadius: 16, fontSize: 13, fontWeight: 600, color: '#2b1b22' }}>
                    {user?.name} (you)
                  </span>
                ) : (
                  <select style={inp} value={cf.doctor_id} onChange={e => setCf(f => ({ ...f, doctor_id: e.target.value }))}>
                    <option value="">— Select doctor (optional) —</option>
                    {doctors.map(d => <option key={d.party_id} value={d.party_id}>{d.name}</option>)}
                  </select>
                )}
              </div>
            </Grid2>
            <Grid3 style={{ marginTop: 14 }}>
              <F label="Diagnosis *"  k="diagnosis"  form={cf} set={setCf} required />
              <F label="ICD-10 Code"  k="icd10_code" form={cf} set={setCf} placeholder="e.g. E11.9" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <F label="Start Date" k="start_date" type="date" form={cf} set={setCf} />
                <F label="End Date"   k="end_date"   type="date" form={cf} set={setCf} />
              </div>
            </Grid3>
            <div style={{ marginTop: 14 }}>
              <label style={lbl}>Care Instructions</label>
              <textarea style={{ ...inp, minHeight: 100 }} value={cf.care_instructions} onChange={e => setCf(f => ({ ...f, care_instructions: e.target.value }))} placeholder="Detailed treatment and care instructions…" />
            </div>
            <FmtActions saving={saving} onCancel={() => setShowCreate(false)} label="Create Care Plan" color="#94335a" />
          </form>
          </div>
        </Modal>
      )}

      {/* ── Edit Modal ── */}
      {editRow && (
        <Modal title={`Update — ${editRow.diagnosis}`} onClose={() => setEditRow(null)}>
          {eErr && <ErrBox msg={eErr} />}
          <form onSubmit={handleEdit}>
            <Grid2>
              <SelF label="Status" k="status" form={ef} set={setEf} opts={STATUSES.filter(s => s !== 'REVOKED')} />
              <F label="End Date" k="end_date" type="date" form={ef} set={setEf} />
            </Grid2>
            <div style={{ marginTop: 14 }}>
              <label style={lbl}>Care Instructions</label>
              <textarea style={{ ...inp, minHeight: 80 }} value={ef.care_instructions} onChange={e => setEf(f => ({ ...f, care_instructions: e.target.value }))} />
            </div>
            <FmtActions saving={eSaving} onCancel={() => setEditRow(null)} label="Save Changes" color="#10b981" />
          </form>
        </Modal>
      )}

      {/* ── Revoke Confirm ── */}
      {revokeRow && (
        <ConfirmDialog
          message={`Revoke care plan for ${revokeRow.diagnosis}?`}
          detail="This plan will be permanently revoked. Any ongoing visits linked to it will need to be updated."
          onConfirm={handleRevoke}
          onCancel={() => setRevokeRow(null)}
          loading={revokeLoading}
          confirmLabel="Revoke Plan"
        />
      )}
    </div>
  );
}

const btnPrimary = bg => ({ padding: '9px 18px', background: bg, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' });
const btnSm = bg => ({ padding: '5px 10px', background: bg, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' });
const ErrBox = ({ msg }) => <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{msg}</div>;
const Grid2 = ({ children }) => <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>{children}</div>;
const Grid3 = ({ children, style }) => <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, ...style }}>{children}</div>;
const F = ({ label, k, type = 'text', form, set, required, span, placeholder }) => (
  <div style={span ? { gridColumn: '1 / -1' } : {}}>
    <label style={lbl}>{label}</label>
    <input style={inp} type={type} value={form[k]} onChange={e => set(f => ({ ...f, [k]: e.target.value }))} required={required} placeholder={placeholder} />
  </div>
);
const SelF = ({ label, k, form, set, opts }) => (
  <div>
    <label style={lbl}>{label}</label>
    <select style={inp} value={form[k]} onChange={e => set(f => ({ ...f, [k]: e.target.value }))}>
      <option value="">— Select —</option>
      {opts.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
    </select>
  </div>
);
const FmtActions = ({ saving, onCancel, label, color = '#94335a' }) => (
  <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
    <button type="submit" disabled={saving} style={{ padding: '9px 22px', background: color, color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving…' : label}</button>
    <button type="button" onClick={onCancel} style={{ padding: '9px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
  </div>
);
const Pagination = ({ page, total, per, onPage }) => {
  const pages = Math.ceil(total / per); if (pages <= 1) return null;
  return <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 20 }}>
    <button onClick={() => onPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pgBtn}>‹</button>
    <span style={{ padding: '6px 12px', fontSize: 13, color: '#475569' }}>Page {page}/{pages}</span>
    <button onClick={() => onPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={pgBtn}>›</button>
  </div>;
};
const pgBtn = { padding: '6px 14px', background: '#f1f5f9', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, cursor: 'pointer' };
